// apps/frontend/src/lib/connection-manager.ts
import type { PipelineNode, PipelineEdge, PipelineNodeType } from '@/pages/pipelines/types/pipeline';
import type { ConnectionValidation, ValidationSeverity } from './validation-types';

/**
 * Node type order for sequence validation.
 * Lower index = earlier in pipeline flow.
 */
const NODE_TYPE_ORDER: Record<PipelineNodeType, number> = {
  SOURCE: 0,
  TRANSFORMATION: 1,
  FILTER: 2,
  AGGREGATION: 3,
  JOIN: 4,
  UNION: 5,
  DESTINATION: 6,
  MULTI_SPLIT: 7,
  TIERED_DESTINATION: 8,
};

/**
 * Validates a connection between two nodes with a permissive approach.
 * Always returns canConnect: true, but provides validation feedback.
 *
 * @param sourceNode - The node from which the connection originates
 * @param targetNode - The node to which the connection points
 * @param existingEdges - Array of existing edges in the pipeline
 * @returns ConnectionValidation object with severity and feedback message
 */
export function validateConnection(
  sourceNode: PipelineNode,
  targetNode: PipelineNode,
  existingEdges: PipelineEdge[]
): ConnectionValidation {
  const sourceType = sourceNode.data.type;
  const targetType = targetNode.data.type;

  // Check 1: DESTINATION nodes cannot be sources
  if (sourceType === 'DESTINATION') {
    return {
      valid: false,
      severity: 'error',
      message: 'DESTINATION nodes cannot be a source for other nodes',
      canConnect: true, // Permissive: always allow
    };
  }

  // Check 2: Duplicate connection detection
  const duplicateExists = existingEdges.some(
    (edge) => edge.source === sourceNode.id && edge.target === targetNode.id
  );

  if (duplicateExists) {
    return {
      valid: false,
      severity: 'warning',
      message: 'Duplicate connection',
      canConnect: true, // Permissive: always allow
    };
  }

  // Check 3: Reverse flow detection (unusual ordering)
  // Exception: JOIN nodes can receive from any node type (they merge streams)
  if (targetType !== 'JOIN') {
    const sourceOrder = NODE_TYPE_ORDER[sourceType];
    const targetOrder = NODE_TYPE_ORDER[targetType];

    if (sourceOrder > targetOrder) {
      return {
        valid: false,
        severity: 'warning',
        message: `Unusual flow direction: ${sourceType} → ${targetType}. Consider reordering nodes.`,
        canConnect: true, // Permissive: always allow
      };
    }
  }

  // Valid connection
  return {
    valid: true,
    severity: 'info',
    message: `Valid connection: ${sourceType} → ${targetType}`,
    canConnect: true,
  };
}

/**
 * Returns edge styling based on validation severity.
 * Uses Tailwind color palette for consistent UI.
 *
 * @param severity - Validation severity level
 * @returns Style object with stroke color and optional dash pattern
 */
export function getEdgeStyle(severity: ValidationSeverity): {
  stroke: string;
  strokeWidth?: number;
  strokeDasharray?: string;
} {
  const baseStyle = {
    strokeWidth: 2,
  };

  switch (severity) {
    case 'error':
      return {
        ...baseStyle,
        stroke: '#ef4444', // Tailwind red-500
      };

    case 'warning':
      return {
        ...baseStyle,
        stroke: '#eab308', // Tailwind yellow-500
        strokeDasharray: '5,5',
      };

    case 'info':
      return {
        ...baseStyle,
        stroke: '#3b82f6', // Tailwind blue-500
      };
  }
}
